import SvgLayer from "./SvgLayer";
import React, { useEffect, useState, useRef } from "react";
import { DraggableCore, DraggableData } from "react-draggable";
import {
  MouseTouchEvent,
  MouseEvent,
} from "react-draggable/build/cjs/utils/types";

import { getOffsetProc, key2pos, offsetToKey } from "../chessUtils/util";
import { Z_UNDER } from "./animations";
import DrawLayer from "./DrawLayer";
import pieceStyles from "./Piece.module.css";
import { Key } from "./types";
import { MoveShort, Square } from "@chesshotel/chess";

const isSafari =
  process.browser &&
  /Safari/.test(navigator.userAgent) &&
  /Apple Computer/.test(navigator.vendor);

export const isRightButton = (e: MouseTouchEvent): boolean =>
  e.buttons === 2 || e.button === 2;

export default function DragBoard({
  freeMove,
  lastPuzzleMove,
  puzzleDone,
  pieceRefs,
  movePiece,
  possibleMoves,
  squareSize,
  playingWhite,
  position,
  highLightKey,
  bestMove,
  chessTermArrows,
  showLastMove,
  lastMove,
  lastMoveColor,
  settings,
  moveType,
  moveTypeKey,
  children,
  highlightSquares,
}: {
  freeMove: boolean;
  pieceRefs: any;
  possibleMoves: any;
  squareSize: number;
  playingWhite: boolean;
  highLightKey: Key | null;
  movePiece: any;
  position: any;
  bestMove: MoveShort | null;
  chessTermArrows: any;
  showLastMove: boolean;
  lastMove: any;
  lastMoveColor: any;
  settings: any;
  lastPuzzleMove: any;
  puzzleDone: boolean;
  moveType: any;
  moveTypeKey: Key | null;
  highlightSquares: Square[];
  children: React.ReactNode;
}) {
  const disableInteractions = !squareSize;
  const halfSquare = squareSize / 2;
  const [goast, setGoast] = useState(null);
  const [moveHelp, setMoveHelp] = useState(null);
  const [moveSquarePos, setMoveSquarePos] = useState(null);
  const [shapes, setShapes] = useState([]);

  const [drawMode, setDrawMode] = useState(false);

  // Use refs instead of module-level variables to prevent stuck pieces
  const lastDragKeyRef = useRef<Key | null>(null);
  const currentKeyRef = useRef<Key | null>(null);
  const dragIdRef = useRef<string | null>(null);

  function canMove(to: Key) {
    const moves = possibleMoves[currentKeyRef.current];
    return Array.isArray(moves) && moves.indexOf(to) >= 0;
  }

  useEffect(() => {
    if (dragIdRef.current && possibleMoves[currentKeyRef.current]) {
      if (canMove(lastDragKeyRef.current as Key)) {
        const dragPos = key2pos(lastDragKeyRef.current as Key);
        setMoveSquarePos(dragPos);
      }
    }
  }, [possibleMoves]);

  function onDrag(e, data) {
    if (!dragIdRef.current) {
      return;
    }

    const posProc = {
      x: ((data.x - halfSquare) * 100) / squareSize,
      y: ((data.y - halfSquare) * 100) / squareSize,
    };

    const pieceRef = pieceRefs.current[dragIdRef.current];
    // piece.setOffset(posProc);
    if (pieceRef) {
      pieceRef.style.transform = `translate(${posProc.x}%, ${posProc.y}%)`;
    }
    const dragKey = offsetToKey(
      data.x,
      data.y,
      playingWhite,
      squareSize
    ) as Key;

    if (lastDragKeyRef.current != dragKey) {
      if (canMove(dragKey)) {
        setMoveSquarePos(key2pos(dragKey));
      } else {
        setMoveSquarePos(null);
      }
      lastDragKeyRef.current = dragKey;
    }
  }

  function onStop(e: MouseTouchEvent, data: DraggableData) {
    if (!dragIdRef.current) {
      return;
    }
    setMoveSquarePos(null);

    const to = offsetToKey(data.x, data.y, playingWhite, squareSize) as Key;
    const pieceRef = pieceRefs.current[dragIdRef.current];
    if (canMove(to)) {
      const goal = getOffsetProc(to, playingWhite);
      if (pieceRef) {
        pieceRef.style.transform = `translate(${goal.x}%, ${goal.y}%)`;
      }
      movePiece({ from: currentKeyRef.current, to });
    } else {
      const goal = getOffsetProc(currentKeyRef.current, playingWhite);
      if (pieceRef) {
        pieceRef.style.transform = `translate(${goal.x}%, ${goal.y}%)`;
      }
    }

    if (pieceRef && pieceRefs.current[dragIdRef.current]) {
      pieceRefs.current[dragIdRef.current].style.zIndex = Z_UNDER;
    }

    setGoast(null);
    settings.pieceDestinations && setMoveHelp(null);
    // Clear refs to prevent stuck state
    lastDragKeyRef.current = null;
    dragIdRef.current = null;
    currentKeyRef.current = null;
  }

  function onStartDrag(e: MouseTouchEvent, data: DraggableData) {
    if (isSafari) {
      e.preventDefault();
    }
    if (!position) {
      return;
    }
    const startKey = offsetToKey(
      data.x,
      data.y,
      playingWhite,
      squareSize
    ) as Key;
    currentKeyRef.current = startKey;
    const piece = position[startKey];
    if (
      piece &&
      (freeMove ||
        (playingWhite && piece.color === "white") ||
        (!playingWhite && piece.color === "black"))
    ) {
      const { color, role } = piece;
      dragIdRef.current = piece.id;
      const pieceRef = pieceRefs.current[piece.id];
      if (pieceRef) {
        pieceRef.style.zIndex = 300;
      }
      setGoast({
        goastPos: key2pos(startKey),
        goastPiece: { color, role },
      });

      settings.pieceDestinations && setMoveHelp(possibleMoves[startKey]);
    }
  }

  function startDraw(x, y) {
    const currentPos = [Math.floor(x / squareSize), Math.floor(y / squareSize)];
    setShapes([...shapes, { from: currentPos }]);
  }

  function onDraw(e: MouseTouchEvent, data: DraggableData) {
    const currentPos = [
      Math.floor(data.x / squareSize),
      Math.floor(data.y / squareSize),
    ];
    const newShapes = [...shapes];
    newShapes[newShapes.length - 1].to = currentPos;
    setShapes(newShapes);
  }
  function onStopDraw(e: MouseTouchEvent, data: DraggableData) {}

  const boardContent = (
    <div
      className={`${
        playingWhite ? "" : pieceStyles.flipped
      } flex w-full h-full`}
    >
      <DrawLayer
        lastPuzzleMove={lastPuzzleMove}
        goast={goast}
        moveSquarePos={moveSquarePos}
        moveHelp={moveHelp}
        position={position}
        highLightKey={highLightKey}
        playingWhite={playingWhite}
        showLastMove={showLastMove}
        lastMove={lastMove}
        lastMoveColor={lastMoveColor}
        settings={settings}
        puzzleDone={puzzleDone}
        moveType={moveType}
        moveTypeKey={moveTypeKey}
        highlightSquares={highlightSquares}
      />
      <SvgLayer
        shapes={shapes}
        bestMove={bestMove}
        chessTermArrows={chessTermArrows}
        playingWhite={playingWhite}
      />
      {children}
    </div>
  );

  if (disableInteractions) {
    return boardContent;
  }

  return (
    <>
      <DraggableCore
        enableUserSelectHack={false}
        onStart={(e: MouseTouchEvent, data: DraggableData) => {
          if (!e.shiftKey) {
            onStartDrag(e, data);
          }
        }}
        onDrag={(e: MouseTouchEvent, data: DraggableData) => {
          drawMode ? onDraw(e, data) : onDrag(e, data);
        }}
        onStop={(e: MouseTouchEvent, data: DraggableData) => {
          drawMode ? onStopDraw(e, data) : onStop(e, data);
        }}
        onMouseDown={(e: MouseEvent) => {
          if (e.shiftKey) {
            setDrawMode(true);
            startDraw(e.nativeEvent.layerX, e.nativeEvent.layerY);
          } else {
            setDrawMode(false);
            setShapes([]);
          }
        }}
      >
        {boardContent}
      </DraggableCore>
    </>
  );
}
